import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { BastionApiClient, BastionApiError } from '@bastion/api';
import { authenticatedFetch } from '../authenticatedFetch';
import { getSupabaseAccessToken } from '../supabase';
import { deriveCameraFiles, episodeMediaFiles } from '../utils/cameraFiles';
import type { CloudEpisode } from '../types';
import type { EpisodeSegment } from './episodeAnnotations/types';
import { invalidateEpisodeCollectionQueries } from './episodeCollectionQueries';
import { readCloudEpisode } from './useCloudEpisodes';
import { readSegments } from './episodeAnnotations/segments';
import {
  cloudEpisodeKey,
  episodeSegmentsKey,
} from './episodeAnnotations/keys';

export interface ReviewQueueEpisode {
  episode_id: string;
  collector_id: string;
  created_at: string;
  task_description: string | null;
  task_rating: number | null;
  frame_count: number | null;
  fps: number | null;
  s3_uri: string | null;
  sop_id: string;
  sop_version: number;
  hostname: string | null;
  hardware_version: string | null;
}

export interface ReviewQueueResponse {
  episodes: ReviewQueueEpisode[];
  pending_count: number;
  next_cursor: string | null;
}

export interface CompleteReviewInput {
  episodeId: string;
  rating: 0 | 1 | 3 | 5;
  notes?: string | null;
  tags?: string[];
}

export interface CompleteReviewResponse {
  episode_id: string;
  snapshot_id: string;
  next_episode_id: string | null;
}

export const reviewQueueKey = (limit: number) =>
  ['episodeReviewQueue', limit] as const;

export function removeEpisodeFromReviewQueue(
  queue: ReviewQueueResponse,
  episodeId: string,
): ReviewQueueResponse {
  const episodes = queue.episodes.filter(
    (episode) => episode.episode_id !== episodeId,
  );
  const removed = episodes.length < queue.episodes.length;
  return {
    ...queue,
    episodes,
    pending_count: removed
      ? Math.max(0, queue.pending_count - 1)
      : queue.pending_count,
  };
}

export function useReviewQueue(
  limit = 3,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: reviewQueueKey(limit),
    enabled: options.enabled ?? true,
    queryFn: () =>
      authenticatedFetch<ReviewQueueResponse>(
        `/episodes/review-queue?limit=${limit}`,
      ),
    refetchInterval: 5_000,
    staleTime: 2_000,
  });
}

export function useCompleteEpisodeReview(limit = 3) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, ...body }: CompleteReviewInput) =>
      authenticatedFetch<CompleteReviewResponse>(
        `/episodes/${encodeURIComponent(episodeId)}/review/complete`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onError: (error) => {
      if (
        error instanceof BastionApiError &&
        (error.status === 400 || error.status === 409)
      ) {
        void queryClient.invalidateQueries({ queryKey: ['episodeReviewQueue'] });
      }
    },
    onSuccess: async (_response, variables) => {
      await queryClient.cancelQueries({ queryKey: ['episodeReviewQueue'] });
      const key = reviewQueueKey(limit);
      const current = queryClient.getQueryData<ReviewQueueResponse>(key);
      if (current) {
        queryClient.setQueryData(
          key,
          removeEpisodeFromReviewQueue(current, variables.episodeId),
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['episodeReviewQueue'] }),
        invalidateEpisodeCollectionQueries(queryClient),
      ]);
    },
  });
}

/**
 * Warm the caches the review view reads, for the next episode in the queue.
 *
 * Every read here seeds the cache entry its own hook serves, so each one calls
 * that hook's own reader: `readCloudEpisode` for the episode and `readSegments`
 * for the timeline. A reader of its own here would seed an entry the hook then
 * serves unchecked, and `readSegments` is where the truncation warning lives.
 *
 * The routes return the episode only when the caller's organization holds a
 * grant on it, and answer 404 otherwise. `ReviewPage` swallows the failure,
 * because a prefetch that fails costs a warm cache and nothing else.
 */
export async function prefetchReviewEpisode(
  queryClient: QueryClient,
  episodeId: string,
): Promise<void> {
  const episode = await queryClient.fetchQuery({
    queryKey: cloudEpisodeKey(episodeId),
    queryFn: (): Promise<CloudEpisode | null> => readCloudEpisode(episodeId),
    staleTime: 30_000,
  });
  // Must match the consumer's derivation exactly or the presigned-URL
  // query key misses and this prefetch is wasted.
  const cameraFiles = episodeMediaFiles(deriveCameraFiles(episode?.hardware), {
    hasAudio: episode?.has_audio,
    hasTranscript: episode?.has_transcript,
  });
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: episodeSegmentsKey(episodeId),
      queryFn: (): Promise<EpisodeSegment[]> =>
        readSegments(episodeId, ['manual', 'ai']),
      staleTime: 10_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['presignedUrls', episodeId, cameraFiles],
      queryFn: async (): Promise<Record<string, string>> => {
        const token = await getSupabaseAccessToken();
        const response = await new BastionApiClient().getPresignedUrls(
          episodeId,
          cameraFiles,
          'download',
          token,
        );
        return response.urls;
      },
      staleTime: 5 * 60_000,
    }),
  ]);
}
